import { useState } from 'react';
import { Modal } from './Modal';
import { api, apiErrorMessage } from '../lib/api';
import { useAuthStore } from '../lib/authStore';
import type { User } from '@stromo/shared';

/**
 * Sperrt die App, bis der User sein Default-Passwort geändert hat.
 * Wird gerendert, sobald `user.password_change_required === true` ist.
 * Esc / Klick auf Overlay sind absichtlich nicht weiterleitet (User
 * kann nicht "wegnavigieren") — der einzige Ausweg ist Speichern.
 */
export function ForcePasswordChangeModal({ user }: { user: User }) {
  const setUser = useAuthStore((s) => s.setUser);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (next.length < 8) {
      setError('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    if (next !== confirm) {
      setError('Bestätigung stimmt nicht mit dem neuen Passwort überein');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post<{ user: User }>('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setUser(data.user);
    } catch (err) {
      setError(apiErrorMessage(err, 'Passwort konnte nicht geändert werden'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Passwort ändern erforderlich"
      onClose={() => undefined /* nicht schliessbar */}
      width={460}
      footer={
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={saving || !current || !next || !confirm}
        >
          {saving ? 'Speichere …' : 'Passwort ändern'}
        </button>
      }
    >
      <p style={{ fontSize: 14, color: 'var(--color-text)', lineHeight: 1.55 }}>
        Hallo {user.first_name}, dein aktuelles Passwort ist noch ein
        Standard- oder ein vom Administrator gesetztes Passwort. Bitte
        wähle jetzt ein eigenes, sicheres Passwort.
      </p>

      {error && <div className="login-error">{error}</div>}

      <div className="form-grid" style={{ gridTemplateColumns: '1fr', marginTop: 14 }}>
        <div className="form-group full">
          <label className="form-label">Aktuelles Passwort</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoFocus
            autoComplete="current-password"
          />
        </div>
        <div className="form-group full">
          <label className="form-label">Neues Passwort</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            placeholder="mindestens 8 Zeichen"
          />
        </div>
        <div className="form-group full">
          <label className="form-label">Neues Passwort wiederholen</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && current && next && confirm) handleSubmit();
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
